import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { toast } from "sonner";

const FMT = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });

const STATUS_BADGE = {
  active: "bg-emerald-50 text-emerald-700",
  trialing: "bg-blue-50 text-blue-700",
  past_due: "bg-rose-50 text-rose-700",
  canceled: "bg-stone-100 text-stone-700",
  incomplete: "bg-amber-50 text-amber-700",
  free: "bg-stone-100 text-stone-500",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function SuperadminBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/superadmin/billing/overview`);
        if (!cancel) setData(r.data);
      } catch (e) { if (!cancel) toast.error(formatApiError(e)); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return <div className="px-10 py-12 text-[var(--ink-3)]">Loading…</div>;
  if (!data) return null;

  const t = data.totals || {};

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="superadmin-billing-page">
      <div className="mb-10">
        <div className="overline mb-2">Superadmin · Billing</div>
        <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>Platform billing</h1>
        <p className="text-[var(--ink-2)] mt-2">MRR + plan distribution across every organization.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <div className="card p-5">
          <div className="overline mb-2">Monthly Recurring Revenue</div>
          <div className="font-mono text-3xl text-[var(--ink)]" data-testid="mrr-total">
            {FMT.format(t.mrr_cents / 100)}
          </div>
          <div className="text-[12px] text-[var(--ink-3)] mt-1">excludes trials</div>
        </div>
        <div className="card p-5">
          <div className="overline mb-2">Paying orgs</div>
          <div className="font-mono text-3xl">{t.paid_count}</div>
          <div className="text-[12px] text-[var(--ink-3)] mt-1">of {t.paid_count + t.free_count} total</div>
        </div>
        <div className="card p-5">
          <div className="overline mb-2">Plan mix</div>
          <div className="space-y-0.5 text-[12px]">
            <div>Enterprise: <b>{t.by_tier?.enterprise || 0}</b></div>
            <div>Pro: <b>{t.by_tier?.pro || 0}</b></div>
            <div>Starter: <b>{t.by_tier?.starter || 0}</b></div>
            <div className="text-[var(--ink-3)]">Free: {t.by_tier?.free || 0}</div>
          </div>
        </div>
        <div className="card p-5">
          <div className="overline mb-2">Status mix</div>
          <div className="space-y-0.5 text-[12px]">
            {Object.entries(t.by_status || {}).map(([s, n]) => (
              <div key={s} className="capitalize">{s.replace(/_/g, " ")}: <b>{n}</b></div>
            ))}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="report-table">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Plan</th>
              <th>Status</th>
              <th>MRR</th>
              <th>Renews / ends</th>
              <th>Trial ends</th>
              <th>Stripe customer</th>
            </tr>
          </thead>
          <tbody>
            {(data.orgs || []).map((o) => {
              const status = o.subscription_status || "free";
              return (
                <tr key={o.org_id} data-testid={`billing-org-${o.org_id}`}>
                  <td className="font-medium text-[var(--ink)]">{o.name}</td>
                  <td>{o.plan_tier ? <span className="capitalize">{o.plan_tier}</span> : <span className="text-[var(--ink-3)]">—</span>}</td>
                  <td>
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_BADGE[status] || "bg-stone-100"}`}>
                      {status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="font-mono text-[13px]">{o.mrr_display}</td>
                  <td className="text-[13px] text-[var(--ink-2)]">
                    {o.cancel_at_period_end && o.current_period_end ? <span className="text-rose-700">Cancels {fmtDate(o.current_period_end)}</span>
                      : fmtDate(o.current_period_end)}
                  </td>
                  <td className="text-[13px] text-[var(--ink-2)]">{fmtDate(o.trial_end)}</td>
                  <td className="text-[11px] text-[var(--ink-3)] font-mono">{o.stripe_customer_id || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
